/*
 * The logistics API's failures, kept whole: every refused write carries the
 * HTTP status, the server's error code and message, and the reason the server
 * gave — so the page can say WHY a save failed instead of a bare "Couldn't
 * save the delivery". `logisticsErrorReason` (client/logistics) turns one into
 * the i18n line a toast or alert shows.
 */

import { z } from 'zod';
import {
  DELIVERY_CONFLICT_REASONS,
  DELIVERY_VALIDATION_REASONS,
  type DeliveryConflictReason,
  type DeliveryJoinConflictReason,
  type DeliveryValidationReason,
} from '../../logistics/reasons.js';

/** The five ways a booking can be refused a place on an existing order
 *  (contract v11; v13 dropped `SOURCE_EXISTS`, since a task already on the
 *  line now adds to its share). They ride the same `details.reason` channel
 *  as the order conflicts, so the client reads it exactly once. */
export type DeliverySourceConflictReason = DeliveryJoinConflictReason;

/** Every delivery-write 409 reason — the literals the backend throws with,
 *  from the one shared list, so a typo'd comparison (e.g. in
 *  `moveCardErrorTitleKey`) fails to compile. */
export type DeliveryOrderConflictReason = DeliveryConflictReason;

/** The reasons the client infers itself, from a refusal the server words
 *  generically: a status name already on the board (a 400 naming `name`),
 *  a status cards still sit in (the delete 409), an amount over the cap (a
 *  schema `too_big` issue). */
const INFERRED_REASONS = ['status-name-taken', 'status-in-use', 'quantity-too-large'] as const;

/** Every reason a logistics failure can name: the order conflicts, the
 *  delivery 400s the server names in `details.reason`, and the ones above. */
export type LogisticsFailureReason =
  | DeliveryOrderConflictReason
  | DeliveryValidationReason
  | (typeof INFERRED_REASONS)[number];

/** What the server said about a refused request. `R` narrows the reason for
 *  an error class that only ever carries a known set. */
export interface ILogisticsFailure<R extends string = string> {
  status: number;
  /** The envelope's `error.code` (`VALIDATION_ERROR`, `CONFLICT`, …). */
  code?: string;
  /** The envelope's `error.message` — English, for logs, never for the UI. */
  serverMessage: string;
  /** The reason as the server named it (`details.reason`) or as its
   *  validation issues imply it — see `readLogisticsFailure`. */
  reason?: R;
  /** The order that blocks this one (an `unsynced-rival` 409). */
  ref?: string;
  /** How many deliveries still use a status (a `status-in-use` 409). */
  count?: number;
  /** The material a refusal is about (a `unit-not-stock-unit` 400). */
  itemName?: string;
  /** The raw `error.details`, for a caller that knows its endpoint's shape. */
  details?: unknown;
}

/** Any logistics request the server refused. */
export class LogisticsApiError<R extends string = string> extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly serverMessage: string;
  readonly reason: R | undefined;
  readonly ref: string | undefined;
  readonly count: number | undefined;
  readonly itemName: string | undefined;

  constructor(message: string, failure: ILogisticsFailure<R>) {
    super(message);
    this.name = 'LogisticsApiError';
    this.status = failure.status;
    this.code = failure.code;
    this.serverMessage = failure.serverMessage;
    this.reason = failure.reason;
    this.ref = failure.ref;
    this.count = failure.count;
    this.itemName = failure.itemName;
  }
}

/** Thrown when the requested site (or order) is not visible to the user (HTTP 404). */
export class LogisticsNotFoundError extends LogisticsApiError {
  constructor(failure: Partial<ILogisticsFailure> = {}) {
    super('logistics: site not found', { serverMessage: '', ...failure, status: 404 });
    this.name = 'LogisticsNotFoundError';
  }
}

/** A delivery-order write refused by one of the order rules (HTTP 409). Its
 *  `reason` is the server's `details.reason` when the client knows it — see
 *  `DeliveryOrderConflictReason`; undefined for a newer server's reason. */
export class DeliveryOrderConflictError extends LogisticsApiError<DeliveryOrderConflictReason> {
  constructor(reason?: DeliveryOrderConflictReason, failure: Partial<ILogisticsFailure> = {}) {
    super('the one-unsynced-order rule refused this write', {
      serverMessage: '',
      ...failure,
      status: 409,
      reason,
    });
    this.name = 'DeliveryOrderConflictError';
  }
}

/** A status still holding deliveries cannot be deleted (HTTP 409). */
export class StatusInUseError extends LogisticsApiError {
  constructor(count?: number, failure: Partial<ILogisticsFailure> = {}) {
    super('deliveries still use this status', {
      serverMessage: '',
      ...failure,
      status: 409,
      reason: 'status-in-use',
      count,
    });
    this.name = 'StatusInUseError';
  }
}

/** A narrowing to one known set: the reason itself when the set holds it,
 *  undefined for anything else (a newer server's reason, a missing one). */
function knownIn<T extends string>(list: readonly T[]): (reason: string | undefined) => T | undefined {
  const known: ReadonlySet<string> = new Set(list);
  return (reason) => (reason !== undefined && known.has(reason) ? (reason as T) : undefined);
}

/** The 409's reason when it is one the client knows; anything else (a newer
 *  server's reason, a missing one) is the generic conflict. */
export const conflictReasonOf = knownIn<DeliveryOrderConflictReason>(DELIVERY_CONFLICT_REASONS);

/** Any failure's reason when it is one the client knows — what a caller
 *  switches over exhaustively. */
export const failureReasonOf = knownIn<LogisticsFailureReason>([
  ...DELIVERY_CONFLICT_REASONS,
  ...DELIVERY_VALIDATION_REASONS,
  ...INFERRED_REASONS,
]);

/** One request-schema issue, as the backend's validation envelope lists it. */
const IssueSchema = z.object({ keyword: z.unknown(), instancePath: z.unknown() });
type IValidationIssue = z.infer<typeof IssueSchema>;

const AMOUNT_PATH = /\/(quantity|amount)$/;
const DATE_PATH = /\/(requestedDate|answeredDate|dueDate)$/;

/** The reason a schema 400 implies: an amount over its `max` is the cap; a
 *  cross-field (`custom`) refusal on a date is the date order. */
function issueReason(issues: IValidationIssue[]): LogisticsFailureReason | undefined {
  const on = (keyword: string, path: RegExp) =>
    issues.some((i) => i.keyword === keyword && typeof i.instancePath === 'string' && path.test(i.instancePath));
  if (on('too_big', AMOUNT_PATH)) return 'quantity-too-large';
  return on('custom', DATE_PATH) ? 'date-order' : undefined;
}

/** A field of the error envelope that reads as absent when it has the wrong
 *  type, so one odd field never costs the rest of the failure. */
const lenient = <T extends z.ZodType>(schema: T) => schema.optional().catch(undefined);

/** The API's error envelope. `details` stays raw (`unknown`) for a caller
 *  that knows its endpoint's shape; the two shapes read here are below. */
const ErrorEnvelopeSchema = z.object({
  error: z.object({ code: lenient(z.string()), message: lenient(z.string()), details: z.unknown() }),
});

/** A `details` object: the reason the server named, and its companions. */
const NamedDetailsSchema = z.object({
  reason: lenient(z.string()),
  ref: lenient(z.string()),
  count: lenient(z.number()),
  itemName: lenient(z.string()),
});

/** A `details` array: the schema's own validation issues. */
const IssuesSchema = z.array(IssueSchema);

/** A failed response, read once into its failure. A body that is missing,
 *  not JSON, or not the error envelope still yields the status. */
export async function readLogisticsFailure(res: Response): Promise<ILogisticsFailure> {
  const envelope = ErrorEnvelopeSchema.safeParse(await res.json().catch(() => null));
  if (!envelope.success) return { status: res.status, serverMessage: '' };
  const { code, message, details } = envelope.data.error;
  const named = NamedDetailsSchema.safeParse(details);
  const issues = IssuesSchema.safeParse(details);
  return {
    status: res.status,
    code,
    serverMessage: message ?? '',
    reason: named.success ? named.data.reason : issues.success ? issueReason(issues.data) : undefined,
    ref: named.success ? named.data.ref : undefined,
    count: named.success ? named.data.count : undefined,
    itemName: named.success ? named.data.itemName : undefined,
    details,
  };
}

/** The plain failure for a write: what was attempted, the status and the
 *  server's message — the text these errors have always carried. */
export function apiFailure(what: string, failure: ILogisticsFailure): LogisticsApiError {
  return new LogisticsApiError(`${what} failed: ${failure.status} ${failure.serverMessage}`, failure);
}
